import type { ImpressionSource } from '@dupe/common/types'
import { createContext, useContext, useMemo } from 'react'
import type { ChatResultPathway } from '@dupe/common/constants'
import type { RouterCategoryGroup } from '@dupe/common/affiliateRouter'
import type { SortCriteria } from '@dupe/common/types'
import type { DupeInputType } from '~/src/hooks/useGetDupes'

/**
 * Carries the active sort + the `default_sort_variant` arm slot down to the
 * leaf `ProductListCard`s so their impression (`Result Viewed`) and click
 * (`Dupe Result Clicked`) events can be tagged with `sort_criteria` +
 * `default_sort_variant` — for both PostHog and Tinybird (`analytics_events`).
 *
 * The arm slot was built for `rerank-default-sort` (PostHog experiment 374206),
 * which concluded LOST and was removed on 2026-09-16; `defaultSortVariant` has
 * been permanently `null` since. The transport is kept deliberately — the
 * columns behind it hold historical per-arm rows that readouts still query, and
 * the next experiment on this surface plugs straight into it. See
 * `useSortCriteriaState`.
 *
 * Also carries the server-derived input modality (`input_type`) so the same
 * leaf events become natively breakable by modality in PostHog (which can't
 * hash-join to Tinybird's `visual_matches.input_type`).
 *
 * A context (rather than prop threading) avoids passing these through the
 * intermediate `ProductList` layer. Provided by each result surface
 * (`ProductRow`, `ProductColumnCarousel`, `BrainResultsDrawer`); consumers
 * outside a provider get `{ null, null }` and fall back to today's behavior.
 *
 * Note: `default_sort_variant` used to be registered as a PostHog super
 * property in `useSortCriteriaState` as well, so PostHog events carried the arm
 * whether or not they passed through here. That registration was removed with
 * the experiment, and `AppProviders` now actively `unregister`s the copy left
 * behind in returning visitors' browser storage. This explicit payload field
 * outlives it because Tinybird never received super properties at all.
 */
/**
 * The `sort_criteria` a surface stamps: any requestable `SortCriteria`, plus
 * the partner-pin test's reported-only `'partnerPin'` (pin2 views on the
 * served-order lens — kept out of `ctr_by_cell`'s `'recommended'` bucket).
 * See `PARTNER_PIN_SORT_CRITERIA` in `components/chat/organicOrder.ts`.
 */
export type StampedSortCriteria = SortCriteria | 'partnerPin'

export interface ResultSortContextValue {
  sortCriteria: StampedSortCriteria | null
  defaultSortVariant: string | null
  /** False while the surface's sort state is still settling. Leaf cards defer
   * their `Result Viewed` impression until this is true, so a CTR baseline
   * isn't mis-tagged by an order that changes moments after first paint. The
   * gate was built for `rerank-default-sort`'s cold-load flag flip and outlived
   * it: the legacy `/[hash]` page still feeds it the readiness of the v1
   * `sponsored-picks-rail` flag, and any future async sort signal has the same
   * failure mode. Chat surfaces pass `useSortCriteriaState().isReady`, which is
   * now constant true. Defaults to true so consumers outside a provider are
   * unaffected. */
  sortReady: boolean
  /** Server-derived input modality of the search that produced these results
   * (`url` | `image` | `null`). Stamped as `input_type` onto the leaf
   * impression/click events. Null when the surface has no modality (chat
   * messages) or outside a provider. */
  inputType: DupeInputType
  /** Product-grid origin for v2 chat funnel attribution. */
  pathway: ChatResultPathway | null
  /** Source product's results hash when this grid came from a pasted product. */
  sourceHash: string | null
  /** Which placement these cards render in: the sponsored `rail` vs the organic
   * `grid`. Stamped as `section` onto the leaf impression/click events. It was
   * added so `sponsored-picks-rail-v2` could compute rail-vs-grid CTR and
   * index-in-rail — `pick` alone does not identify a rail once it is backfilled
   * with non-pick best matches. That rail was deleted on 2026-09-16, so no
   * surface emits `'rail'` today: `ProductRow` passes `'grid'`, the marketplaces
   * surfaces pass `'marketplace'` (DUP-2048), the v3 inline picks row passes
   * `'inline'`, and everything else leaves it null. */
  section: 'rail' | 'grid' | 'marketplace' | 'inline' | null
  /** Commercial lane of the search that produced these results, stamped onto
   * the leaf click-out as `router_category_group` so `link_clicks.metadata`
   * carries a category on the grid the way the toggler surface already does.
   *
   * ⚠️ On the toggler (`src='instant-search-broadcast'`) this field means "the
   * lane the affiliate router ROUTED through", and always co-occurs with
   * `router_version` / `router_merchant`. Here it means only "the lane this
   * search classified into" — no routing decision was made, so those two stay
   * null. **`src` is the discriminator**; never pool the two surfaces on this
   * field without it. (`queryTogglerReport` already scopes by `src` for the
   * same reason.)
   *
   * ⚠️ Produced by `toRouterCategoryGroup`, so it is only ever
   * `furniture_home` | `fashion` | `beauty` | null — NOT the full
   * `ShoppingCategory`. Categories outside those lanes (electronics, toys, …)
   * read null, and so does an unclassified search; the two are not
   * distinguishable here. ⛔ Do not switch this to `toTrackedCategoryGroup` to
   * fix that: its extra `'other'` value is absent from the server's
   * `ROUTER_CATEGORY_GROUP_SET`, so every `other` row would be silently nulled
   * on arrival. Widening the taxonomy means adding a lane to
   * `ROUTER_CATEGORY_GROUPS` first.
   *
   * ⚠️ Grid rows also never carry `rugs` — that is a promotion the router
   * applies from query text, and `toRouterCategoryGroup` never emits it. */
  routerCategoryGroup: RouterCategoryGroup | null
  /**
   * Which store's surface these cards came from. Null on the organic grid.
   *
   * ⛔ **TWO VOCABULARIES, and you must normalise before using it.** An earlier
   * version of this comment promised `wayfair | ebay | etsy` on the rail only.
   * That is false in both halves and believing it shipped a bug:
   *
   *  - `MarketplacesRail` passes a SLUG (`'wayfair'`), and tranche 2 added ten
   *    more beyond the original three.
   *  - `RetailerStackDrawer` passes `stack.key`, an ORIGIN or lowercased
   *    retailer name (`'wayfair.com'`), for **any** retailer with a stack —
   *    Amazon and Overstock included, neither of which is a marketplace.
   *
   * So the live PostHog `marketplace` property already carries both spellings.
   * So the live PostHog `marketplace` property already carries both spellings,
   * and a consumer must normalise before comparing them. Typed `string` rather
   * than a union for that reason: it is not a slug yet.
   *
   * ⚠️ This field is NOT what `link_clicks` records. Store identity there is
   * the top-level `shop_key` column, and the surface is `impression_source`
   * below — neither is derived from this. See DUP-2048.
   *
   * ⚠️ This has to live on the context rather than on the card, because
   * `ProductListCard` reads `section` from here and nowhere else — a new rail
   * cannot stamp its own placement onto leaf events without a provider field.
   * Without it, per-store CTR is unanswerable, which is the question the
   * feature exists to answer.
   */
  marketplace: string | null
  /**
   * Which stores were OFFERED on this turn, comma-joined in pill order
   * (`wayfair,etsy,ebay`). Null outside the Marketplaces rail.
   *
   * ⚠️ **The denominator, and the reason it must ride on every leaf event.**
   * The business question is which marketplace earns per click. Once the pill
   * set is category-dependent (DUP-2048), a per-store rate computed from taps
   * alone is not comparable across stores: Wayfair is only offered on
   * furniture-ish turns, so its tap rate would look superb purely because it is
   * never shown where it would lose. Without the offered set on the event there
   * is no way to reconstruct each store's true denominator after the fact, and
   * the first readout is uninterpretable rather than merely noisy.
   *
   * ⛔ Do not derive this at analysis time from the turn's category — that
   * reconstructs what the config said TODAY, not what the shopper was actually
   * shown when the event fired.
   */
  marketplacesOffered: string | null
  /**
   * Which CONTAINER these cards render inside. Finer-grained than `section`,
   * and the two are not interchangeable: a marketplace tab row and a stack
   * drawer row are both `section: 'marketplace'`, but a tab row was on screen
   * while a drawer row cost the shopper a tap to reach. Pooling them makes the
   * per-store denominator mean two different things at once.
   *
   * ⚠️ **Load-bearing for the impression join, not just for reporting.**
   * `ctr_by_cell` joins clicks to impressions on `(session_id, hash,
   * result_index)`, and a stack drawer re-uses the TURN's hash with a
   * stack-local index — so without a container discriminator on the row, a
   * drawer click at index 2 can attribute to the organic grid's impression at
   * position 2. Those cells feed `ranking_scores` via `computeRankingScores`,
   * so the mis-join would reach production ranking, not just a dashboard.
   */
  impressionSource: ImpressionSource | null
}

const ResultSortContext = createContext<ResultSortContextValue>({
  sortCriteria: null,
  defaultSortVariant: null,
  sortReady: true,
  inputType: null,
  pathway: null,
  sourceHash: null,
  section: null,
  routerCategoryGroup: null,
  marketplace: null,
  marketplacesOffered: null,
  impressionSource: null,
})

export function ResultSortProvider({
  sortCriteria,
  defaultSortVariant,
  sortReady = true,
  inputType = null,
  pathway = null,
  sourceHash = null,
  section = null,
  routerCategoryGroup = null,
  marketplace = null,
  marketplacesOffered = null,
  impressionSource = null,
  children,
}: Partial<
  Pick<
    ResultSortContextValue,
    | 'sortReady'
    | 'inputType'
    | 'pathway'
    | 'sourceHash'
    | 'section'
    | 'routerCategoryGroup'
    | 'marketplace'
    | 'marketplacesOffered'
    | 'impressionSource'
  >
> &
  Omit<
    ResultSortContextValue,
    | 'sortReady'
    | 'inputType'
    | 'pathway'
    | 'sourceHash'
    | 'section'
    | 'routerCategoryGroup'
    | 'marketplace'
    | 'marketplacesOffered'
    | 'impressionSource'
  > & {
    children: React.ReactNode
  }) {
  const value = useMemo(
    () => ({
      sortCriteria,
      defaultSortVariant,
      sortReady,
      inputType,
      pathway,
      sourceHash,
      section,
      routerCategoryGroup,
      marketplace,
      marketplacesOffered,
      impressionSource,
    }),
    [
      sortCriteria,
      defaultSortVariant,
      sortReady,
      inputType,
      pathway,
      sourceHash,
      section,
      routerCategoryGroup,
      marketplace,
      marketplacesOffered,
      impressionSource,
    ]
  )
  return (
    <ResultSortContext.Provider value={value}>
      {children}
    </ResultSortContext.Provider>
  )
}

export function useResultSortContext(): ResultSortContextValue {
  return useContext(ResultSortContext)
}

export default ResultSortContext
